/**
 * Integrytas Group — TranslationsEditor (common).
 *
 * Multi-locale editor for the Translation-Mapping pattern (§8). Locale options are
 * the active languages (useLanguageOptions), shown by NAME (§17.B4). On a NEW code
 * (empty value) it pre-fills one row per active language, the user's language first
 * (item 10). "Auto-translate" fills empty non-primary rows from the primary text via
 * aiClient (best-effort, fail-open).
 *
 * Controlled: parent passes value + onChange and upserts each row into the `_tl`
 * table on save (onConflict `<pk>,locale_code`).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageOptions } from '../../lib/use_language_options';
import { aiClient } from '../../lib/ai/ai_client';

export interface TranslationRow {
  locale_code: string;
  display_text: string;
  description?: string;
}

export interface TranslationsEditorProps {
  value: TranslationRow[];
  onChange: (rows: TranslationRow[]) => void;
  /**
   * Applied instead of onChange when the component SEEDS rows itself (the
   * prefill below). Seeding is not a user edit, and routing it through onChange
   * made forms that track dirty state report unsaved changes the moment they
   * opened — see the prefill comment. Parents that track dirty pass their raw
   * setter here; without it seeding still falls back to onChange.
   */
  onSeed?: (rows: TranslationRow[]) => void;
  /** Optional override of the offered locale codes; defaults to all active languages. */
  availableLocales?: string[];
  showDescription?: boolean;
  label?: string;
  /** Pre-fill one row per active language when value is empty (default true). */
  prefillAllOnEmpty?: boolean;
}

export function TranslationsEditor({
  value,
  onChange,
  onSeed,
  availableLocales,
  showDescription = false,
  label,
  prefillAllOnEmpty = true,
}: TranslationsEditorProps) {
  const { t } = useTranslation();
  const { options, primary } = useLanguageOptions();
  const resolvedLabel = label ?? t('common.translations.label', 'Translations');

  // Offered locales: explicit override, else the active languages (primary first).
  const localeOptions = useMemo(() => {
    if (availableLocales && availableLocales.length > 0) {
      return availableLocales.map(code => ({ code, name: options.find(o => o.code === code)?.name ?? code }));
    }
    return options;
  }, [availableLocales, options]);

  const nameOf = (code: string) => localeOptions.find(o => o.code === code)?.name ?? code;
  const used = useMemo(() => new Set(value.map(r => r.locale_code)), [value]);
  const cols = showDescription ? 4 : 3;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (i: number, patch: Partial<TranslationRow>) =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const add = () => {
    const next = localeOptions.find(o => !used.has(o.code))?.code ?? '';
    onChange([...value, { locale_code: next, display_text: '', description: '' }]);
  };

  // Item 10: pre-fill all active languages on a NEW code (empty value), primary first.
  //
  // `value` is ALSO empty for a moment while an edit form loads its _tl rows, so
  // this fires on existing records too. That is survivable for the rows (the
  // fetch overwrites them), but it must not be reported as an edit: routed
  // through onChange it marked the form dirty on open, and closing a drill-down
  // the user had not touched warned them about losing changes that did not
  // exist. Seeding therefore goes to onSeed when the parent supplies one.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    if (!prefillAllOnEmpty) return;
    if (value.length > 0) { prefilled.current = true; return; } // edit mode — leave as-is
    if (localeOptions.length === 0) return;                     // options not loaded yet
    const ordered = [...localeOptions].sort((a, b) => (a.code === primary ? -1 : b.code === primary ? 1 : 0));
    (onSeed ?? onChange)(ordered.map(o => ({ locale_code: o.code, display_text: '', description: '' })));
    prefilled.current = true;
  }, [localeOptions, primary, prefillAllOnEmpty, value.length, onSeed]); // eslint-disable-line

  const primaryText = value.find(r => r.locale_code === primary)?.display_text?.trim() ?? '';
  const emptyTargets = value.filter(r => r.locale_code !== primary && !r.display_text.trim());
  const canAutoTranslate = primaryText.length > 0 && emptyTargets.length > 0;

  const autoTranslate = async () => {
    setBusy(true); setError(null);
    try {
      if (!primaryText) throw new Error(t('common.translations.auto_translate_no_primary', 'Enter the primary language text first.'));
      const next = [...value];
      for (const target of emptyTargets) {
        const res = await aiClient.translate([primaryText], target.locale_code);
        const translated = res.data?.translations?.[0];
        if (res.error || !translated) throw new Error(res.error?.message ?? t('common.translations.auto_translate_failed', 'Auto-translate failed.'));
        const idx = next.findIndex(r => r.locale_code === target.locale_code);
        if (idx >= 0) next[idx] = { ...next[idx], display_text: translated };
      }
      onChange(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-csm-sm font-medium text-csm-ink-soft">{resolvedLabel}</span>
        <div className="flex items-center gap-2">
          {canAutoTranslate && (
            <button type="button" onClick={() => void autoTranslate()} disabled={busy}
              className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt disabled:opacity-60">
              {busy ? t('common.translations.auto_translate_busy', 'Translating…') : t('common.translations.auto_translate', 'Auto-translate')}
            </button>
          )}
          <button type="button" onClick={add}
            className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">
            {t('common.translations.add_language', '+ Add language')}
          </button>
        </div>
      </div>
      {error && (
        <div className="mb-1 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-2xs text-csm-danger">{error}</div>
      )}
      <table className="w-full border-collapse text-csm-sm">
        <thead>
          <tr className="text-csm-2xs uppercase tracking-wide text-csm-ink-soft">
            <th className="px-2 py-1 text-left">{t('common.translations.language', 'Language')}</th>
            <th className="px-2 py-1 text-left">{t('common.translations.display_text', 'Display Text')}</th>
            {showDescription && <th className="px-2 py-1 text-left">{t('common.translations.description', 'Description')}</th>}
            <th />
          </tr>
        </thead>
        <tbody>
          {value.map((r, i) => (
            <tr key={i}>
              <td className="px-2 py-0.5">
                <select value={r.locale_code} onChange={e => update(i, { locale_code: e.target.value })}
                  className="h-7 w-40 rounded border border-csm-border px-1 text-csm-sm">
                  {r.locale_code && !localeOptions.some(o => o.code === r.locale_code) && (
                    <option value={r.locale_code}>{nameOf(r.locale_code)}</option>
                  )}
                  {localeOptions.map(o => (
                    <option key={o.code} value={o.code} disabled={used.has(o.code) && o.code !== r.locale_code}>{o.name}</option>
                  ))}
                </select>
              </td>
              <td className="px-2 py-0.5">
                <input value={r.display_text} onChange={e => update(i, { display_text: e.target.value })}
                  className="h-7 w-full rounded border border-csm-border px-1 text-csm-sm" />
              </td>
              {showDescription && (
                <td className="px-2 py-0.5">
                  <input value={r.description ?? ''} onChange={e => update(i, { description: e.target.value })}
                    className="h-7 w-full rounded border border-csm-border px-1 text-csm-sm" />
                </td>
              )}
              <td className="px-2 py-0.5 text-right">
                <button type="button" onClick={() => remove(i)} className="text-csm-xs text-csm-danger" title={t('common.remove', 'Remove')}>×</button>
              </td>
            </tr>
          ))}
          {value.length === 0 && (
            <tr>
              <td colSpan={cols} className="px-2 py-2 text-csm-xs italic text-csm-watermark">
                {t('common.translations.empty', 'No translations — add at least one language.')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
